import React from 'react';

import { Collapse, Grid } from '@arco-design/web-react';
import { InputWithUnitField } from '@extensions/components/Form';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { VerticalAlign } from '@extensions/AttributePanel/components/attributes/VerticalAlign';
import { BorderWidth } from '@extensions/AttributePanel/components/attributes/BorderWidth';
import { BorderStyle } from '@extensions/AttributePanel/components/attributes/BorderStyle';
import { BorderColor } from '@extensions/AttributePanel/components/attributes/BorderColor';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { BackgroundColor } from '../../attributes';
import { TextStyle, useBlock, useFocusIdx } from '@bay-email/editor';
import {
	t,
	isDynamicListingColumn,
	getAncestorListingWrapper,
} from '@bay-email/core';
import { pixelAdapter } from '../../adapter';
import { BorderWidthControl } from '../shared/BorderWidthControl';
import { BorderRadiusControl } from '../shared/BorderRadiusControl';
import { AttachedListingSection } from '../shared/AttachedListingSection';
import { useInsideListingScope } from '../shared/useInsideListingScope';
import { PinnedListingPicker } from '../shared/PinnedListingPicker';
import {
	blockTreeUsesListingMergeTags,
	collectBlockMergeTagValues,
} from '../shared/listingAttachment';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

const columnWidthPercentAdapter = {
	format(value?: string) {
		if (!value) return '';
		return String(value).replace(/%$/, '');
	},
	parse(value?: string) {
		const numericValue = String(value ?? '')
			.replace(/\D+/g, '')
			.slice(0, 3);

		if (!numericValue) return undefined;

		return `${Math.min(Number(numericValue), 100)}%`;
	},
};

export function Column() {
	const { focusIdx } = useFocusIdx();
	const { focusBlock, values } = useBlock();
	const listingMergeTagValues = React.useMemo(
		() => collectBlockMergeTagValues(focusBlock),
		[focusBlock],
	);
	// Inside a Listing Wrapper the wrapper's listing cascades to everything, so
	// this column's own listing (if it came from a "Listing" section dropped in
	// there) is moot and its picker stays hidden.
	const insideListingWrapper = React.useMemo(
		() => Boolean(getAncestorListingWrapper((values as any)?.content, focusIdx)),
		[values, focusIdx],
	);
	const ownsListing = isDynamicListingColumn(focusBlock) && !insideListingWrapper;
	// The legacy per-block picker renders nothing inside a listing scope, so the
	// tags-only branch below must not put a header around it. `ownsListing` is
	// unaffected — that branch shows PinnedListingPicker, not the attached one.
	const insideListingScope = useInsideListingScope();
	const usesListingTags = React.useMemo(
		() => !insideListingScope && blockTreeUsesListingMergeTags(focusBlock),
		[focusBlock, insideListingScope],
	);
	// A column in Listing mode keeps its listing whether or not a field uses it
	// yet — picking the listing first and adding fields after is the normal flow.
	useClearAttachedListingWhenUnused(focusBlock, usesListingTags || ownsListing);

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={
				usesListingTags || ownsListing ? ['-1', '0', '1'] : ['0', '1']
			}
			style={{ padding: 0 }}
		>
			{/* A listing column outside any wrapper carries its own listing (from the
			    sidebar's "Listing" section variant), so it gets the picker. Anything
			    else falls back to the legacy per-block attachment. */}
			{(ownsListing || usesListingTags) && (
				<Collapse.Item
					name='-1'
					header={t('Listing Settings')}
					contentStyle={{ padding: 0 }}
				>
					{ownsListing ? (
						<PinnedListingPicker />
					) : (
						<AttachedListingSection observedValues={listingMergeTagValues} />
					)}
				</Collapse.Item>
			)}
			<Collapse.Item
				name='0'
				header={t('Dimension')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '16px' }}
					>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<InputWithUnitField
									label={t('Width (%)')}
									name={`${focusIdx}.attributes.width`}
									config={columnWidthPercentAdapter}
									inputMode='numeric'
									maxLength={3}
									onChangeAdapter={value =>
										String(value ?? '')
											.replace(/\D+/g, '')
											.slice(0, 3)
									}
									validate={value => {
										if (!value) return undefined;

										const parsedValue = Number(String(value).replace(/\D+/g, ''));
										if (
											Number.isNaN(parsedValue) ||
											parsedValue < 0 ||
											parsedValue > 100
										) {
											return t('Width must be between 0 and 100');
										}

										return undefined;
									}}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<Padding />
					<VerticalAlign hiddenValues={['middle']} />
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='1'
				header={t('Styling')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '12px' }}
					>
						<Grid.Col span={12}>
							<BackgroundColor />
						</Grid.Col>
					</Grid.Row>
					<div style={{ marginBottom: '12px' }}>
						<TextStyle variation='strong'>{t('Border')}</TextStyle>
					</div>
					<Grid.Row gutter={16}>
						<Grid.Col span={16}>
							<BorderColor secondary />
						</Grid.Col>
						<Grid.Col span={8}>
							<BorderStyle secondary />
						</Grid.Col>
					</Grid.Row>
					<BorderWidthControl />
					<BorderRadiusControl />
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='4'
				header={t('Extra')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
